<template>
    <div class="pt-1" v-if="reservation && metadata.has_property_management_module">
        <b-row class="pt-4" v-if="reservation.commission_property_choices.length">
            <b-col>
                <responsive-button
                    v-if="metadata.permissions.commissions_edit"
                    :icon="['fas', 'chart-pie']"
                    variant="outline-primary"
                    :responsiveness="false"
                    :withSpace="false"
                    @click="$bvModal.show(`setup-commissions-modalId-${reservation.id}`)"
                >
                    Setup Commissions
                </responsive-button>
            </b-col>
        </b-row>
        <grid
            v-if="reservation.commissions.length"
            ref="grid"
            class="guest-invoices-grid pt-2"
            :columnDefs="columnDefs"
            :rowData="reservation.commissions"
            :headerHeight="45"
            :isAutoWidth="true"
            :isAutoHeight="true"
            :isSearch="false"
            :with-space="false"
        >
        </grid>
        <setup-commissions-modal
            v-if="reservation.commission_property_choices.length"
            :modalId="`setup-commissions-modalId-${reservation.id}`"
            :reservation="reservation"
            :isSetup="true"
            @refreshReservation="refreshReservation"
        />
        <b-row
            v-if="
                metadata.permissions.commissions_edit &&
                reservation.channel_commission !== false &&
                reservation.state != 'Cancelled'
            "
            class="pt-4 channel-commission"
            no-gutters
        >
            <b-col class="mr-2" cols="auto">
                <b>Channel Commission:</b>
            </b-col>
            <b-col cols="auto">
                <validation-provider
                    v-if="channelCommission.isEdit"
                    v-click-outside="handleOutsideClick"
                    name="channel_commission"
                    v-slot="{ errors, handleChange }"
                    rules="validPositive|validFloat|maxValue:100|required"
                    
                >
                    <form-input
                        v-focus
                        min="0"
                        max="100"
                        step="0.01"
                        type="number"
                        v-model="reservation.channel_commission"
                        :error="errors[0]"
                        @input="handleChange"
                    />
                </validation-provider>
                <span v-else type="button" class="text-primary clickable-cell" @click="editChannelCommission">
                    {{ channelCommissionDisplay }}
                </span>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { GUEST_RESERVATION_UPDATE_CHANNEL_COMMISSION_ENDPOINT } from '../../../../../endpoints';
import API from '../../../../../api.jsx';
import Grid from '../../../../generic/grid/grid.vue';
import FormInput from '../../../../generic/form-input/form-input.vue';
import ResponsiveButton from '../../../../generic/responsive-button/responsive-button.vue';
import ActionCellRenderer from './action-cell-renderer.vue';
import SetupCommissionsModal from './setup-commissions-modal.vue';
import { isMediumScreen } from '../../../../../utils/screens';
import { toAmountDisplay } from '../../../../../utils/numbers';
import { formatResponseError } from '../../../../../utils/api.js';

export default {
    name: 'GuestInvoicesCommissionsTab',
    components: {
        Grid,
        ResponsiveButton,
        FormInput,
        SetupCommissionsModal,
        actionCellRenderer: ActionCellRenderer,
    },
    data() {
        return {
            channelCommission: { previous: null, isEdit: false },
        };
    },
    inject: ['showSuccessToast', 'showErrorToast'],
    computed: {
        ...mapGetters('guest', ['metadata', 'reservation']),
        guestId() {
            return parseInt(this.$route.params.id);
        },
        columnDefs() {
            return [
                {
                    field: 'rental_property',
                    pinned: 'left',
                    minWidth: 175,
                    width: isMediumScreen() ? undefined : 175,
                    autoWidthExclude: this.isMobile,
                    headerName: 'Rental Property',
                },
                {
                    field: 'display_commission',
                    headerName: 'Total',
                    minWidth: 100,
                },
                {
                    field: 'commission_calculation_display',
                    headerName: 'Calculation',
                    minWidth: 150,
                },
                {
                    field: 'details[0].display',
                    headerName: 'Default',
                    valueGetter: ({ data }) => data.details[0].display,
                    minWidth: 250,
                },
                {
                    field: 'details[1].display',
                    headerName: '#1',
                    valueGetter: ({ data }) => data.details[1].display,
                    minWidth: 250,
                },
                {
                    field: 'details[2].display',
                    headerName: '#2',
                    valueGetter: ({ data }) => data.details[2].display,
                    minWidth: this.isMobile ? 250 : undefined,
                    autoWidthExclude: !this.isMobile,
                },
                {
                    field: '',
                    headerName: '',
                    editable: false,
                    sortable: false,
                    minWidth: 54,
                    maxWidth: 54,
                    cellRendererParams: {
                        refreshReservation: this.refreshReservation,
                    },
                    cellStyle: { paddingRight: 0, paddingLeft: 0 },
                    cellRenderer: 'actionCellRenderer',
                },
            ];
        },
        channelCommissionDisplay() {
            return toAmountDisplay({
                amount: this.reservation.channel_commission,
                percentage: true,
            });
        },
        isMobile() {
            const md = 768;
            return window.innerWidth < md || window.innerHeight < md;
        },
    },
    methods: {
        refreshReservation(callback) {
            this.$emit('refreshReservations', callback);
        },
        handleOutsideClick(event) {
            this.channelCommission.isEdit = false;
            this.handleChannelCommissionChange();
        },
        editChannelCommission() {
            this.channelCommission.isEdit = true;
            this.channelCommission.previous = this.reservation.channel_commission;
        },
        async handleChannelCommissionChange() {
            const previous = this.channelCommission.previous;
            const newChannelCommission = this.reservation.channel_commission;
            if (newChannelCommission < 0) {
                this.reservation.channel_commission = previous;
                return;
            }
            try {
                this.$emit('loading', true);
                if (newChannelCommission != previous) {
                    await API.patch(
                        GUEST_RESERVATION_UPDATE_CHANNEL_COMMISSION_ENDPOINT(this.reservation.id, this.guestId),
                        { channel_percentage: newChannelCommission }
                    );
                    this.showSuccessToast({
                        text: 'Channel commission successfully updated.',
                    });
                }
            } catch (e) {
                this.reservation.channel_commission = previous;
                const { generic, fields } = formatResponseError(e);
                this.showErrorToast({
                    text: generic || fields.channel_percentage[0] || 'Could not change channel commission.',
                });
            } finally {
                this.$emit('loading', false);
                this.channelCommission.isEdit = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../../guest-invoices-grid.scss';

.channel-commission {
    padding-left: 0.5rem;

    :deep(.row) {
        padding: 0.3rem !important;
    }

    :deep(.col-auto) {
        align-self: center !important;
    }

    :deep(.form-group) {
        max-width: 9rem !important;
        margin-bottom: 0px !important;
    }
}

.clickable-cell {
    text-decoration: underline;
}
</style>
